import * as config from '../config';
import { Passenger } from '../models/passenger';
import { reportError } from '../telemetry';
import {
  API,
  APIErrorMap,
  APIResponse422,
  LoginResponse,
  RedeemVoucherResponse,
  SendVoucherResponse,
  ValidationErrorResponse,
} from './api';
import {
  marshalLoginRequest,
  marshalRedeemVoucherRequest,
  marshalSendVoucherRequest,
  unmarshalBooking,
  unmarshalPassenger,
} from './marshal';
import * as Raven from 'raven-js';

export class FetchAPI implements API {
  private request(path: string, init?: RequestInit): Promise<Response> {
    const headers = { 'Content-Type': 'application/json; charset=utf-8' };
    return fetch(config.apiURL + path, { headers, ...init });
  }

  private async parse422<F extends string>(
    response: Response,
  ): Promise<ValidationErrorResponse<F>> {
    const body = (await response.json()) as APIResponse422;
    const errors = body.errors as APIErrorMap<F>;
    return { status: 'validation-error', errors };
  }

  private isNetworkError(e: Error): boolean {
    if (
      e instanceof TypeError ||
      (e instanceof DOMException && e.name === 'TypeMismatchError')
    )
      // A TypeError is raised by fetch if there is a network error.
      // Seriously. You can check the docs here:
      // https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
      //
      // Older versions of MS Edge (build 14393) throw a TypeMismatchError
      // Which is now deprecated, instead of TypeError
      return true;
    return false;
  }

  // tslint:disable:cyclomatic-complexity
  public async login(pnr: string, lastName: string): Promise<LoginResponse> {
    const request = marshalLoginRequest(pnr, lastName);

    try {
      const response = await this.request('/volaris/vouchers/login/', {
        method: 'POST',
        body: JSON.stringify(request),
      });

      if (response.status === 200) {
        const body = await response.json();
        return { booking: unmarshalBooking(body), status: 'ok' };
      } else if (response.status === 401) {
        return { status: 'not-found' };
      } else if (response.status === 403) {
        return { status: 'ineligible' };
      } else if (response.status === 410) {
        return { status: 'expired' };
      } else if (response.status === 422) {
        return this.parse422(response);
      } else if (response.status === 404 || response.status >= 500) {
        Raven.captureMessage('Unexpected response status', {
          level: 'critical',
          extra: {
            path: '/volaris/vouchers/login/',
            pnr,
            status: response.status,
          },
        });
      }
    } catch (e) {
      if (this.isNetworkError(e)) return { status: 'network-error' };
      reportError(e);
    }

    return { status: 'unexpected-error' };
  }

  public async redeemVoucher(
    pnr: string,
    lastName: string,
    passenger: Passenger,
  ): Promise<RedeemVoucherResponse> {
    try {
      const response = await this.request('/volaris/vouchers/redeem-voucher/', {
        method: 'POST',
        body: JSON.stringify(
          marshalRedeemVoucherRequest(pnr, lastName, passenger),
        ),
      });

      if (response.status === 200)
        return {
          status: 'ok',
          passenger: unmarshalPassenger(await response.json()),
        };
      else if (response.status === 404 || response.status >= 500) {
        Raven.captureMessage('Unexpected response status', {
          level: 'critical',
          extra: {
            path: '/volaris/vouchers/redeem-voucher/',
            pnr,
            status: response.status,
          },
        });
      }
    } catch (e) {
      if (this.isNetworkError(e)) return { status: 'network-error' };
      reportError(e);
    }

    return { status: 'unexpected-error' };
  }

  public async sendVoucher(
    toAddress: string,
    pnr: string,
    lastName: string,
    passenger: Passenger,
  ): Promise<SendVoucherResponse> {
    try {
      const response = await this.request('/volaris/vouchers/send-voucher/', {
        method: 'POST',
        body: JSON.stringify(
          marshalSendVoucherRequest(toAddress, pnr, lastName, passenger),
        ),
      });

      if (response.status === 200) {
        return {
          status: 'ok',
          passenger: unmarshalPassenger(await response.json()),
        };
      } else if (response.status === 422) {
        return this.parse422(response);
      } else if (response.status === 404 || response.status >= 500) {
        Raven.captureMessage('Unexpected response status', {
          level: 'critical',
          extra: {
            path: '/volaris/vouchers/redeem-voucher/',
            pnr,
            status: response.status,
          },
        });
      }
    } catch (e) {
      if (this.isNetworkError(e)) return { status: 'network-error' };
      reportError(e);
    }

    return { status: 'unexpected-error' };
  }
}



// WEBPACK FOOTER //
// ../node_modules/source-map-loader!./src/api/fetch.ts